import State from "@/helpers/pattern/state";
import { Se } from "@/models/operativos/se";
import { modelIsModified } from "@/helpers/vue-mc";
import seAPI from "@/services/operativos/se";
import CombosAPI from "@/services/catalogos";
import { EventBus } from "@/events/event-bus";
import * as events from "@/constants/event-names";
import { clearSwal } from "@/constants/sweet-alerts";
import { scrollToForm } from "@/helpers/form-scroll-to";
import { setSelectsInactiveData, leadingZero, fixSe } from "@/helpers/form-common";
import StringMask from "string-mask";

/**
 * State Pattern for se form
 */
export default class SeStates extends State {
  /**
   * General Methods
   */
  base() {
    return {
      clear({ $setState }) {
        $setState("initial");
      }
      , async askForClear({ $data }) {
        try {
          if ((await $data.$swal(clearSwal)).value) this.clear();
        } catch (error) {
          console.error(error);
        }
      }
      , resetModel({ $data }) {
        $data.se = new Se();
        $data.refreshkey += 1;
      }
      , resetValidators({ $data }) {
        $data.$v.$reset();
        $data.refreshkey += 1;
      }
      , isDisableButtonSubmit({ $data }) {
        return $data.hasErrors($data.$v.se) || $data.view.action.submit.busy || !modelIsModified($data.se);
      }
      , async loadSelectsData({ $data }) {
        const { data: { results: { combo: provinciaList } } } = await CombosAPI.get({ option: "provincia" });
        $data.view.elem.select.provincia.filteredList = provinciaList ? provinciaList.filter((value) => value.status) : [];
        $data.view.elem.select.provincia.completeList = provinciaList || [];

        const { data: { results: { combo: acuerdoList } } } = await CombosAPI.get({ option: "acuerdo" });
        $data.view.elem.select.acuerdo.filteredList = acuerdoList ? acuerdoList.filter((value) => value.status) : [];
        $data.view.elem.select.acuerdo.completeList = acuerdoList || [];

        const { data: { results: { combo: subindustriaList } } } = await CombosAPI.get({ option: "subindustria" });
        $data.view.elem.select.subindustria.filteredList = subindustriaList ? subindustriaList.filter((value) => value.status) : [];
        $data.view.elem.select.subindustria.completeList = subindustriaList || [];

        const { data: { results: { combo: partnerList } } } = await CombosAPI.get({ option: "partner" });
        $data.view.elem.select.partner.filteredList = partnerList ? partnerList.filter((value) => value.status) : [];
        $data.view.elem.select.partner.completeList = partnerList || [];
      }
      , async loadSelectLocalidad({ $data }, provincia = $data.se.provincia) {
        if (provincia) {
          const { data: { results: { combo: localidadList } } } = await CombosAPI.getByParameter({ option: "LocalidadesByProvincia", parameter: provincia });
          $data.view.elem.select.localidad.filteredList = localidadList ? localidadList.filter((value) => value.status) : [];
          $data.view.elem.select.localidad.completeList = localidadList || [];
        }
      }
      , relationNull(_data, relation) {
        return relation ? Number(relation) : null;
      }
    };
  }

  /**
   * States and their methods
   */
  states() {
    return {
      initial: {
        $constructor: function ({ $data }) {
          // Reset Models and validators
          this.resetModel();
          this.loadSelectsData();
          this.resetValidators();

          // View configuration buttons
          $data.view.elem.button.submit.class.current = $data.view.elem.button.submit.class.save;
          $data.view.elem.button.submit.title.current = $data.view.elem.button.submit.title.save;
        }
        , async submit(
          { $data },
          {
            se,
            seller,
            storeid,
            negocio,
            nombre,
            razonsocial,
            cuit,
            direccion,
            altura,
            provincia,
            localidad,
            referencia,
            codigopostal,
            email,
            telefono,
            acuerdo,
            subindustria,
            partner,
            latitud,
            longitud,
            geoRelevancia,
            grupocontrol,
            photos,
            status
          }
        ) {
          try {
            if (!$data.isAllValid($data.$v)) {
              EventBus.$emit(events.SHOW_TOASTER_MESSAGE, "error", "VL0000", $data.$t("errors.validof"));
              scrollToForm($data);
              return false;
            }

            $data.view.action.submit.busy = true;

            const response = await seAPI.save({
              clave: null
              , se: ["PROP", "OPTBLUE", "PAYFAC"].includes(negocio) && se ? se : null
              , seller: ["OPTBLUE", "PAYFAC", "PAYFACSID"].includes(negocio) && seller ? leadingZero(seller, 30) : null
              , storeid: ["PAYFACSID"].includes(negocio) && storeid ? storeid : null
              , negocio
              , nombre
              , razonsocial
              , cuit: cuit ? cuit.replaceAll(/\D/g, "") : ""
              , direccion
              , altura
              , provincia: this.relationNull(provincia)
              , localidad: this.relationNull(localidad)
              , referencia
              , codigopostal
              , email
              , telefono
              , acuerdo: this.relationNull(acuerdo)
              , subindustria: this.relationNull(subindustria)
              , partner: this.relationNull(partner)
              , latitud
              , longitud
              , geoRelevancia
              , grupocontrol
              , photos: photos && photos.length > 0 ? photos.map((item) => item.join(":")).join(",") : null
              , status
            });

            $data.view.action.submit.busy = false;
            this.resetModel();
            this.resetValidators();
            $data.$refs.seTable.refreshData();
            return response;
          } catch (error) {
            console.error(error);
            $data.view.action.submit.busy = false;
            return error;
          }
        }
      }
      , editing: {
        $constructor: function (
          { $data },
          {
            clave,
            se,
            seller,
            storeid,
            negocio,
            nombre,
            razonsocial,
            cuit,
            direccion,
            altura,
            provincia,
            localidad,
            referencia,
            codigopostal,
            email,
            telefono,
            acuerdo,
            subindustria,
            partner,
            latitud,
            longitud,
            geoRelevancia,
            grupocontrol,
            photos,
            status
          }
        ) {
          // View configuration buttons
          $data.view.elem.button.submit.class.current = $data.view.elem.button.submit.class.update;
          $data.view.elem.button.submit.title.current = $data.view.elem.button.submit.title.update;

          // Set options to combos if not are active
          setSelectsInactiveData(provincia, $data.view.elem.select.provincia.completeList, $data.view.elem.select.provincia.filteredList);
          setSelectsInactiveData(localidad, $data.view.elem.select.localidad.completeList, $data.view.elem.select.localidad.filteredList);
          setSelectsInactiveData(acuerdo, $data.view.elem.select.acuerdo.completeList, $data.view.elem.select.acuerdo.filteredList);
          setSelectsInactiveData(subindustria, $data.view.elem.select.subindustria.completeList, $data.view.elem.select.subindustria.filteredList);
          setSelectsInactiveData(partner, $data.view.elem.select.partner.completeList, $data.view.elem.select.partner.filteredList);

          $data.se = new Se({
            clave
            , se: ["PROP", "OPTBLUE", "PAYFAC"].includes(negocio) && se ? se : null
            , seller: ["OPTBLUE", "PAYFAC", "PAYFACSID"].includes(negocio) && seller ? fixSe(seller) : null
            , storeid: ["PAYFACSID"].includes(negocio) && storeid ? storeid : null
            , negocio
            , nombre
            , razonsocial
            , cuit: StringMask.apply(cuit, "##-########-#")
            , direccion
            , altura
            , provincia: this.relationNull(provincia)
            , localidad: this.relationNull(localidad)
            , referencia
            , codigopostal
            , email
            , telefono
            , acuerdo: this.relationNull(acuerdo)
            , subindustria: this.relationNull(subindustria)
            , partner: this.relationNull(partner)
            , latitud
            , longitud
            , geoRelevancia
            , grupocontrol
            , photos: photos ? photos.split(",").map((item) => item.split(":")) : null
            , status
          });

          this.resetValidators();
          scrollToForm($data);
        }
        , async submit(
          { $data, $setState },
          {
            clave,
            se,
            seller,
            storeid,
            negocio,
            nombre,
            razonsocial,
            cuit,
            direccion,
            altura,
            provincia,
            localidad,
            referencia,
            codigopostal,
            email,
            telefono,
            acuerdo,
            subindustria,
            partner,
            latitud,
            longitud,
            geoRelevancia,
            grupocontrol,
            photos,
            status
          }
        ) {
          try {
            if (!$data.isAllValid($data.$v)) {
              EventBus.$emit(events.SHOW_TOASTER_MESSAGE, "error", "VL0000", $data.$t("errors.validof"));
              scrollToForm($data);
              return false;
            }

            if (!modelIsModified($data.se)) {
              EventBus.$emit(events.SHOW_TOASTER_MESSAGE, "info", "VL0000", $data.$t("errors.nochanges"));
              return false;
            }

            $data.view.action.submit.busy = true;

            const response = await seAPI.update({
              clave
              , se: ["PROP", "OPTBLUE", "PAYFAC"].includes(negocio) && se ? se : null
              , seller: ["OPTBLUE", "PAYFAC", "PAYFACSID"].includes(negocio) && seller ? leadingZero(seller, 30) : null
              , storeid: ["PAYFACSID"].includes(negocio) && storeid ? storeid : null
              , negocio
              , nombre
              , razonsocial
              , cuit: cuit ? cuit.replaceAll(/\D/g, "") : ""
              , direccion
              , altura
              , provincia: this.relationNull(provincia)
              , localidad: this.relationNull(localidad)
              , referencia
              , codigopostal
              , email
              , telefono
              , acuerdo: this.relationNull(acuerdo)
              , subindustria: this.relationNull(subindustria)
              , partner: this.relationNull(partner)
              , latitud
              , longitud
              , geoRelevancia
              , grupocontrol
              , photos: photos && photos.length > 0 ? photos.map((item) => item.join(":")).join(",") : null
              , status
            });

            $data.$refs.seTable.refreshData();
            $data.view.action.submit.busy = false;
            $setState("initial");
            return response;
          } catch (error) {
            console.error(error);
            $data.view.action.submit.busy = false;
            return error;
          }
        }
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/views/operativos/se/se-state.js